import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Box, Button, Divider, Grid, Typography } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { makeStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import FilledInput from '@material-ui/core/FilledInput';
import InputLabel from '@material-ui/core/InputLabel';
import Container from '@material-ui/core/Container';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { sendCommandResponse } from '../store/Alerts/AlertsActions';
import { SetWeightThreshold } from '../Interfaces/Alerts/CommandResponse';
import {
  WeightAlertGroupRecord,
  WeightAlertRecord,
} from '../Interfaces/Alerts/AlertRecord';
import { SET_WGT_THRESHOLDS_API } from '../Interfaces/Alerts/Api';
import { GetString } from './StringResourceManager';
import { fetchAlertByID } from './AlertsGrid/AlertsGrid';
import { ApplicationState } from '../store';
const useStyles = makeStyles(theme => ({
  gridAlign: {
    display: 'flex',
    alignItems: 'center',
  },
  margin: {
    margin: theme.spacing(1),
  },
  textField: {
    width: '25ch',
  },
  box: {
    height: 60,
    display: 'flex',
    padding: theme.spacing(2),
    margin: theme.spacing(1),
  },
  spreadBox: {
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  dialogAction: {
    height: '3rem',
    padding: theme.spacing(3),
  },
  dialogLabel: {
    fontSize: '1.1rem',
    display: 'inline',
  },
  dialogTextBold: {
    fontWeight: 'bold',
  },
  breakWord: {
    wordWrap: 'break-word',
  },
}));
interface Props {
  alert: WeightAlertGroupRecord;
  closeContainer: () => void;
}

const SetThresholdForm: React.FC<Props> = ({ alert, closeContainer }) => {
  const classes = useStyles();
  const [threshold, setThreshold] = useState<number | undefined>();
  const [referenceAlert, setReferenceAlert] =
    useState<WeightAlertRecord>(Object);
  const dispatch: Dispatch<any> = useDispatch();

  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token
  );

  useEffect((): ReturnType<React.EffectCallback> => {
    if (alert.AlertIDs.length > 0) {
      fetchAlertByID(alert.AlertIDs[0], token)
        .then(data => {
          setReferenceAlert(data);
          setThreshold(data.Threshold);
        })
        .catch(error => {
          console.error(error);
        });
    }
    return (): void => {};
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    // Preventing the page from reloading
    event.preventDefault();

    let payload: SetWeightThreshold[] = [];
    alert.AlertIDs.forEach(alertID => {
      payload.push({
        AlertID: alertID,
        Threshold: threshold ?? -1,
        Barcode: alert.Barcode,
        ItemDesc: alert.ItemDesc,
      });
    });

    sendCommandResponse(dispatch, SET_WGT_THRESHOLDS_API, payload);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setThreshold(Number(event.currentTarget.value));
  };

  return (
    <form autoComplete="off" noValidate onSubmit={handleSubmit}>
      <Container>
        <Grid
          container
          spacing={2}
          className={clsx(classes.gridAlign, classes.margin)}
        >
          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('SET_THRESHOLD_ITEM_NAME')}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(classes.dialogLabel, classes.dialogTextBold)}
              gutterBottom
            >
              {alert.ItemDesc}
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('SET_THRESHOLD_ITEM_BARCODE')}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(
                classes.dialogLabel,
                classes.dialogTextBold,
                classes.breakWord,
                classes.textField
              )}
              gutterBottom
            >
              {alert.Barcode}
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('SET_THRESHOLD_WGTS')}(
              {GetString('SET_THRESHOLD_STANDARD_WGT_UNIT')}):{' '}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(
                classes.dialogLabel,
                classes.dialogTextBold,
                classes.breakWord,
                classes.textField
              )}
              gutterBottom
            >
              {referenceAlert?.WeightRecord?.join(', ') ?? ''}
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Divider />
          </Grid>

          <Grid item md={6} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('SET_THRESHOLD_STANDARD_WGT')}
            </Typography>
          </Grid>
          <Grid item md={6} xs={12}>
            <FormControl
              className={clsx(classes.margin, classes.textField)}
              variant="outlined"
              disabled
            >
              <InputLabel>{referenceAlert?.Threshold}</InputLabel>
              <FilledInput
                endAdornment={
                  <InputAdornment position="end">
                    {GetString('SET_THRESHOLD_STANDARD_WGT_UNIT')}
                  </InputAdornment>
                }
              />
            </FormControl>
          </Grid>

          <Grid item md={6} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('SET_THRESHOLD_STANDARD_SET_WGT')}
            </Typography>
          </Grid>
          <Grid item md={6} xs={12}>
            <FormControl
              className={clsx(classes.margin, classes.textField)}
              variant="outlined"
            >
              <OutlinedInput
                value={threshold ?? ''}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    {GetString('SET_THRESHOLD_STANDARD_WGT_UNIT')}
                  </InputAdornment>
                }
                placeholder="Weight"
                type="number"
              />
            </FormControl>
          </Grid>
        </Grid>
        <Divider />
        <Box className={clsx(classes.spreadBox, classes.box)}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            className={classes.dialogAction}
            onClick={closeContainer}
          >
            {GetString('SET_THRESHOLD_ACTION_1')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.dialogAction}
            onClick={closeContainer}
          >
            {GetString('SET_THRESHOLD_ACTION_2')}
          </Button>
        </Box>
      </Container>
    </form>
  );
};

export default SetThresholdForm;
